import { useCallback, useState } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import type { FinancialStatementSmall } from 'api';
import { SecondaryNavigationSelector } from 'components/ui';
import { toString } from 'utilities';

const FINANCIALS_VERSION_SELECTOR_TEST_ID = 'secondary-navigation__selector-financials-version-name';

interface FinancialsVersionSelectorProps {
  disabled?: boolean;
  onVersionChange: (versionId: string) => void;
  selectedVersion: FinancialStatementSmall | undefined;
  versions: FinancialStatementSmall[];
}

const FinancialsVersionSelector = ({
  disabled,
  onVersionChange,
  selectedVersion,
  versions,
}: FinancialsVersionSelectorProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSelect = useCallback(
    (versionId: string) => {
      handleClose();
      onVersionChange(versionId);
    },
    [handleClose, onVersionChange]
  );

  return (
    <>
      <SecondaryNavigationSelector
        data-testid={FINANCIALS_VERSION_SELECTOR_TEST_ID}
        disabled={disabled}
        label="Financials Version"
        onClick={handleOpen}
        value={selectedVersion?.name ?? ''}
      />
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {versions.map(version => (
          <MenuItem
            data-testid={`secondary-navigation__selector-financials-version-option-${toString(version.id)}`}
            key={version.id}
            selected={toString(version.id) === toString(selectedVersion?.id)}
            onClick={() => handleSelect(toString(version.id))}>
            {version.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default FinancialsVersionSelector;
